/**
 * INSTALL-SPEC-ADMIN — the office side of the install-detail question bank
 * (Settings → Install Details). The engine (installSpec.ts) only ever reads the
 * ACTIVE rows through useInstallBank(); this file reads every row (archived
 * too) and makes the changes: save a question, add / move / archive, sections.
 *
 * The one rule that keeps jobs safe: a question's KEY never changes once it
 * exists. Answers on a job are saved under it (`pvc`, `pvc_amt`, `pvc_note`),
 * so a question can be reworded, moved to another section or asked for other
 * equipment without losing a single saved answer. Archive, never delete —
 * Restore brings the question (and the answers still on the jobs) back.
 *
 * Renaming a CHOICE (not deleting it and adding another) carries the new
 * wording everywhere the old one was used: this question's "how much" list and
 * pre-answers, other questions that wait on it ("only after PVC Pipe is New"),
 * and the saved answer on OPEN jobs (pegasus_install_rename_choice — closed
 * jobs keep the words they closed with).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { QuestionRow } from "./installSpec";

export interface SectionAdminRow { key: string; title: string; sort_order: number; active: boolean }
export interface BankAdmin { sections: SectionAdminRow[]; questions: QuestionRow[] }

/** Every section and question, archived included, in order. */
export function useInstallBankAdmin() {
  return useQuery({
    queryKey: ["install_bank_admin"],
    queryFn: async (): Promise<BankAdmin> => {
      const [s, q] = await Promise.all([
        supabase.from("pegasus_install_section").select("key, title, sort_order, active").order("sort_order").order("key"),
        supabase.from("pegasus_install_question").select("*").order("sort_order").order("key"),
      ]);
      if (s.error) throw s.error;
      if (q.error) throw q.error;
      return { sections: (s.data ?? []) as SectionAdminRow[], questions: (q.data ?? []) as QuestionRow[] };
    },
  });
}

/** How many open jobs have an answer for each question (key → count). */
export function useInstallAnswerCounts() {
  return useQuery({
    queryKey: ["install_answer_counts"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase.rpc("pegasus_install_answer_counts");
      if (error) throw error;
      return Object.fromEntries(((data ?? []) as { key: string; open_jobs: number }[]).map((r) => [r.key, r.open_jobs]));
    },
  });
}

async function who(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data?.user?.email ?? null;
}

function useBankInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["install_bank_admin"] });
    void qc.invalidateQueries({ queryKey: ["install_bank"] });
    void qc.invalidateQueries({ queryKey: ["install_answer_counts"] });
  };
}

/** "Return-Air Drop" → "return_air_drop", unique among `taken` (archived keys count — they may come back). */
export function slugKey(label: string, taken: Set<string>): string {
  let base = label.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "question";
  // An answer's amount and detail are saved as <key>_amt / <key>_note — a key must never look like one.
  if (/_(amt|note)$/.test(base)) base = `${base}_q`;
  const reserved = new Set(["reconnects"]);
  let key = base;
  for (let n = 2; taken.has(key) || reserved.has(key); n++) key = `${base}_${n}`;
  return key;
}

/** One question as the editor saves it. `renames` = choice old wording → new wording. */
export interface QuestionSave {
  row: Omit<QuestionRow, "sort_order" | "active"> & { sort_order?: number; active?: boolean };
  isNew: boolean;
  renames: { from: string; to: string }[];
}

export function useSaveInstallQuestion() {
  const invalidate = useBankInvalidate();
  return useMutation({
    mutationFn: async (v: QuestionSave): Promise<{ jobsRenamed: number }> => {
      const by = await who();
      const now = new Date().toISOString();
      const { row } = v;
      if (v.isNew) {
        const { error } = await supabase.from("pegasus_install_question").insert({ ...row, active: true, updated_at: now, updated_by: by });
        if (error) throw error;
        return { jobsRenamed: 0 };
      }
      const { key, ...patch } = row;
      const { error } = await supabase.from("pegasus_install_question").update({ ...patch, updated_at: now, updated_by: by }).eq("key", key);
      if (error) throw error;
      let jobsRenamed = 0;
      if (v.renames.length) {
        // Other questions that wait on this one ("only after PVC Pipe is New (80→90)") follow the new wording.
        const { data: deps } = await supabase.from("pegasus_install_question").select("key, when_answer").not("when_answer", "is", null);
        for (const d of (deps ?? []) as { key: string; when_answer: { key: string; in: string[] } | null }[]) {
          if (!d.when_answer || d.when_answer.key !== key) continue;
          const next = d.when_answer.in.map((x) => v.renames.find((r) => r.from === x)?.to ?? x);
          if (next.join("\u0000") === d.when_answer.in.join("\u0000")) continue;
          const { error: e2 } = await supabase.from("pegasus_install_question")
            .update({ when_answer: { key, in: next }, updated_at: now, updated_by: by }).eq("key", d.key);
          if (e2) throw e2;
        }
        // Open jobs that picked the old wording get the new one.
        for (const r of v.renames) {
          const { data: n, error: e3 } = await supabase.rpc("pegasus_install_rename_choice", { p_key: key, p_old: r.from, p_new: r.to });
          if (e3) throw e3;
          jobsRenamed += Number(n ?? 0);
        }
      }
      return { jobsRenamed };
    },
    onSuccess: invalidate,
  });
}

/** Archive or restore a question. */
export function useSetQuestionActive() {
  const invalidate = useBankInvalidate();
  return useMutation({
    mutationFn: async (v: { key: string; active: boolean }) => {
      const { error } = await supabase.from("pegasus_install_question")
        .update({ active: v.active, updated_at: new Date().toISOString(), updated_by: await who() }).eq("key", v.key);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

/** Move a question up or down inside its section (renumbers the section 10, 20, 30 …). */
export function useMoveInstallQuestion() {
  const invalidate = useBankInvalidate();
  return useMutation({
    mutationFn: async (v: { list: QuestionRow[]; key: string; dir: -1 | 1 }) => {
      const i = v.list.findIndex((x) => x.key === v.key);
      const j = i + v.dir;
      if (i < 0 || j < 0 || j >= v.list.length) return;
      const next = [...v.list];
      [next[i], next[j]] = [next[j], next[i]];
      const results = await Promise.all(next.map((q, n) => q.sort_order === (n + 1) * 10 ? null
        : supabase.from("pegasus_install_question").update({ sort_order: (n + 1) * 10 }).eq("key", q.key)));
      const bad = results.find((r) => r?.error);
      if (bad?.error) throw bad.error;
    },
    onSuccess: invalidate,
  });
}

/** Add a section, rename it, archive / restore it. */
export function useSaveInstallSection() {
  const invalidate = useBankInvalidate();
  return useMutation({
    mutationFn: async (v: { key?: string; title: string; active?: boolean; taken?: Set<string>; sort_order?: number }) => {
      const by = await who();
      const now = new Date().toISOString();
      const title = v.title.trim();
      if (!title) throw new Error("Name the section first");
      if (!v.key) {
        const key = slugKey(title, v.taken ?? new Set());
        const { error } = await supabase.from("pegasus_install_section")
          .insert({ key, title, sort_order: v.sort_order ?? 1000, active: true, updated_at: now, updated_by: by });
        if (error) throw error;
        return;
      }
      const { error } = await supabase.from("pegasus_install_section")
        .update({ title, ...(v.active != null ? { active: v.active } : {}), updated_at: now, updated_by: by }).eq("key", v.key);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

/** Move a section up or down (renumbers 10, 20, 30 …). */
export function useMoveInstallSection() {
  const invalidate = useBankInvalidate();
  return useMutation({
    mutationFn: async (v: { list: SectionAdminRow[]; key: string; dir: -1 | 1 }) => {
      const i = v.list.findIndex((x) => x.key === v.key);
      const j = i + v.dir;
      if (i < 0 || j < 0 || j >= v.list.length) return;
      const next = [...v.list];
      [next[i], next[j]] = [next[j], next[i]];
      const results = await Promise.all(next.map((s, n) => s.sort_order === (n + 1) * 10 ? null
        : supabase.from("pegasus_install_section").update({ sort_order: (n + 1) * 10 }).eq("key", s.key)));
      const bad = results.find((r) => r?.error);
      if (bad?.error) throw bad.error;
    },
    onSuccess: invalidate,
  });
}
